import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';

import AllowanceInput from 'components/hr/shared/AllowanceInput.component';
import useResettingState from 'hooks/useResettingState.hook';
import useTip from 'hooks/useTip.hook';
import { selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { fromEventToName, fromEventToValue } from 'utils/converters/event.util';
import call from 'utils/functions/call.util';
import asObject from 'utils/objects/asObject.util';
import isFn from 'utils/predicates/function/isFn.util';

const StaffAllowanceInput = ({
  allowance: allowanceFromProps,
  category,
  stylist,
  onChange,
  onBlur,
  onFocus,
  index,
}) => {
  const [allowance, setLocalAllowance] = useResettingState(asObject(allowanceFromProps));
  const { val: isDirty, yay: setIsDirty } = useTip();

  const handleChange = useCallback(
    (ev) => {
      if (isFn(ev.persist)) {
        ev.persist();
      }

      call('onChange', onChange, { allowance, index });

      // SEE: https://getslick.atlassian.net/browse/GS-11888
      const value = fromEventToValue(ev);
      const fieldName = fromEventToName(ev);
      // NOTE: allows a decimal, removes all but the first dot, e.g. 1.5.7 -> 1.57
      const replaced = value.replace(/^([^.]*\.[^.]*)\..*$/, '$1').replace(/[^0-9.]/g, '');

      const candidate = { ...allowance, [fieldName]: replaced };

      setIsDirty(!isEqual(candidate, allowanceFromProps));
      setLocalAllowance(candidate);
    },
    [allowance, allowanceFromProps, index, onChange, setIsDirty, setLocalAllowance],
  );

  const handleBlur = useCallback(
    () => void call('onBlur', onBlur, { allowance, stylist, category, isDirty, index }),
    [onBlur, allowance, stylist, category, isDirty, index],
  );
  const handleFocus = useCallback(
    () => void call('onFocus', onFocus, { allowance, index }),
    [onFocus, allowance, index],
  );

  return (
    <AllowanceInput
      name="allowance"
      value={allowance.allowance}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
});

const StaffAllowanceInputHoc = connect(mapStateToProps)(StaffAllowanceInput);

export default StaffAllowanceInputHoc;
